'use client';

import { useCallback, useRef, useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { wsLogger } from '@/shared/lib/logger';
import { debounce } from '../utils';
import { WEBSOCKET_CONFIG } from '../config';
import {
  ENTITY_QUERY_KEYS as entityQueryKeys,
  type DataChangedEvent,
  type BatchEventPayload,
} from '../realtime-sync.types';
import { executeInstantHandlers, clearAllHandlers } from '../realtime-sync.registry';
import { isMutationLocked as checkMutationLock } from '../realtime-sync.locks';

// Re-export types for consumers
export type { DataChangedEvent, InstantHandler } from '../realtime-sync.types';

const { REALTIME_SYNC } = WEBSOCKET_CONFIG;

interface RealtimeSyncState {
  // Mutation lock per entity:id
  pendingMutations: Map<string, number>;
  // Progress throttle timestamps per entity:id
  progressThrottles: Map<string, number>;
  // Pending invalidations (grouped by debounce)
  pendingInvalidations: Map<string, DataChangedEvent>;
  // Last processed timestamp for ordering
  lastProcessedTs: number;
}

export interface UseRealtimeSyncReturn {
  /** Process incoming data_changed event */
  handleDataChanged: (event: DataChangedEvent) => Promise<void>;
}

/**
 * Hook for handling unified data_changed events from backend.
 *
 * This hook should only be called once — in WebSocketProvider.
 * It manages event routing (instant vs debounced), stale event filtering,
 * mutation locks, and progress throttling.
 *
 * To register instant handlers, use `onInstant` from `realtime-sync.registry.ts`.
 */
export function useRealtimeSync(): UseRealtimeSyncReturn {
  const queryClient = useQueryClient();

  const stateRef = useRef<RealtimeSyncState>({
    pendingMutations: new Map(),
    progressThrottles: new Map(),
    pendingInvalidations: new Map(),
    lastProcessedTs: 0,
  });

  // Flush pending invalidations
  const flushInvalidations = useCallback(() => {
    const events = Array.from(stateRef.current.pendingInvalidations.values());
    stateRef.current.pendingInvalidations.clear();

    if (events.length === 0) return;

    // Group by entity
    const byEntity = new Map<string, DataChangedEvent[]>();
    events.forEach((event) => {
      const list = byEntity.get(event.entity) || [];
      list.push(event);
      byEntity.set(event.entity, list);
    });

    // Invalidate queries per entity
    byEntity.forEach((entityEvents, entity) => {
      const queryKeys = entityQueryKeys[entity] || [entity];
      const hasCreated = entityEvents.some((e) => e.action === 'created');
      const ids = entityEvents.map((e) => e.id).filter(Boolean) as string[];

      wsLogger.debug('[RealtimeSync] Invalidating', { entity, queryKeys, ids });

      // Always invalidate list queries
      queryKeys.forEach((key) => {
        queryClient.invalidateQueries({ queryKey: [key, 'list'] });
        queryClient.invalidateQueries({ queryKey: [key] });
      });

      // For update/delete, also invalidate specific items
      if (!hasCreated && ids.length > 0) {
        ids.forEach((id) => {
          queryKeys.forEach((key) => {
            queryClient.invalidateQueries({ queryKey: [key, 'detail', id] });
            queryClient.invalidateQueries({ queryKey: [key, id] });
          });
        });
      }
    });
  }, [queryClient]);

  // Debounced flush
  const debouncedFlush = useMemo(
    () => debounce(flushInvalidations, REALTIME_SYNC.INVALIDATION_DEBOUNCE_MS),
    [flushInvalidations]
  );

  // Check if mutation lock is active for entity:id
  const isMutationLocked = useCallback(
    (entity: string, id?: string): boolean => {
      // Check module-level singleton locks (set by upload components)
      if (checkMutationLock(entity, id)) {
        return true;
      }
      // Check local hook-level locks (legacy)
      const key = `${entity}:${id || '*'}`;
      const unlockTime = stateRef.current.pendingMutations.get(key);
      if (unlockTime && Date.now() < unlockTime) {
        return true;
      }
      // Also check entity-wide lock
      const entityLockTime = stateRef.current.pendingMutations.get(
        `${entity}:*`
      );
      if (entityLockTime && Date.now() < entityLockTime) {
        return true;
      }
      return false;
    },
    []
  );

  // Throttle progress events
  const shouldThrottleProgress = useCallback(
    (event: DataChangedEvent): boolean => {
      if (event.action !== 'progress') return false;

      const key = `${event.entity}:${event.id || 'global'}`;
      const lastTime = stateRef.current.progressThrottles.get(key) || 0;
      const now = Date.now();

      if (now - lastTime < REALTIME_SYNC.PROGRESS_THROTTLE_MS) {
        return true; // Throttle
      }

      stateRef.current.progressThrottles.set(key, now);
      return false;
    },
    []
  );

  // Handle batch events recursively
  const handleBatchEvent = useCallback(
    async (payload: unknown, parentTs: number): Promise<void> => {
      const batchPayload = payload as BatchEventPayload | undefined;
      if (!batchPayload?.events || !Array.isArray(batchPayload.events)) {
        wsLogger.warn('[RealtimeSync] Invalid batch payload');
        return;
      }

      for (const subEvent of batchPayload.events) {
        const syntheticEvent: DataChangedEvent = {
          entity: subEvent.entity,
          action: subEvent.action,
          id: subEvent.id,
          payload: subEvent.payload,
          ts: parentTs,
          v: 1,
        };

        const key = `${subEvent.entity}:${subEvent.id || 'global'}`;
        stateRef.current.pendingInvalidations.set(key, syntheticEvent);
      }

      debouncedFlush();
    },
    [debouncedFlush]
  );

  // Main event handler
  const handleDataChanged = useCallback(
    async (event: DataChangedEvent): Promise<void> => {
      wsLogger.info('[RealtimeSync] Received:', {
        entity: event.entity,
        action: event.action,
        id: event.id,
        instant: event.instant,
      });

      // Ignore stale events
      if (event.ts < stateRef.current.lastProcessedTs) {
        wsLogger.debug('[RealtimeSync] Ignoring stale event', {
          eventTs: event.ts,
          lastTs: stateRef.current.lastProcessedTs,
        });
        return;
      }
      stateRef.current.lastProcessedTs = event.ts;

      // Handle batch events
      if (event.entity === 'batch' && event.action === 'multiple') {
        await handleBatchEvent(event.payload, event.ts);
        return;
      }

      const key = `${event.entity}:${event.id || 'global'}`;

      // Skip if mutation lock is active
      if (isMutationLocked(event.entity, event.id)) {
        wsLogger.debug('[RealtimeSync] Skipping - mutation locked', key);
        return;
      }

      // Instant events - apply directly via shared handler registry
      if (event.instant && event.payload) {
        // Throttle progress events
        if (shouldThrottleProgress(event)) {
          wsLogger.debug('[RealtimeSync] Throttled progress event', key);
          return;
        }

        await executeInstantHandlers(event);
        return;
      }

      // Non-instant - queue for debounced invalidation
      stateRef.current.pendingInvalidations.set(key, event);
      debouncedFlush();
    },
    [
      handleBatchEvent,
      isMutationLocked,
      shouldThrottleProgress,
      debouncedFlush,
    ]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Cancel debounce timer, then flush pending invalidations immediately
      debouncedFlush.cancel();
      flushInvalidations();
      // Clear all state
      stateRef.current.pendingMutations.clear();
      stateRef.current.progressThrottles.clear();
      clearAllHandlers();
    };
  }, [debouncedFlush, flushInvalidations]);

  return {
    handleDataChanged,
  };
}
